import React, { useContext, useEffect, useState, useCallback } from "react";
import ReactDOM from 'react-dom';
import { useDropzone } from 'react-dropzone';
import { AppContext, DispatchContext } from "../AppContext";
import * as actions from "../actions/actions";
import Service from "../services/service";
import { createUUID, formatFileSize, getCookie, showCaptcha, config, resolveNotificationMessage, timeConverter } from '../helpers/constants';
import { getCsrfToken } from '../helpers/api';
import { LinearProgress, Paper, Divider, Box, Typography, Button, TextField, Grid, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableRow, TableCell, Checkbox, FormControlLabel } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import FullScreenPage from "./FullScreenPage";
import { useParams } from "react-router-dom";
import LoadingScreen from './LoadingScreen';
import IconLabel from './IconLabel';
import DividerLabel from './DividerLabel';
import WarningIcon from "@material-ui/icons/ErrorOutline";
import EmailIcon from "@material-ui/icons/Email";
import InputAdornment from "@material-ui/core/InputAdornment";
import FileUploadCards from "./FileUploadCards";
import { ReactComponent as CheckedIcon } from '../assets/icons/checked.svg';
import { ReactComponent as NoSubmissionIcon } from '../assets/icons/calendar_err.svg';
import { ReactComponent as UploadingIcon } from '../assets/icons/uploading.svg';
import { ReactComponent as FileErrorIcon } from '../assets/icons/fileError.svg';


const useStyles = makeStyles((theme) => ({
    paper: {
        width: "100%",
        maxWidth: 800,
        maxHeight: "90vh",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0px 0px 5px 5px rgba(0, 0, 0, 0.25)",
        "&:hover": {
            boxShadow: "0px 0px 7px 7px rgba(0, 0, 0, 0.4)",
            transition: "box-shadow 0.3s ease-in-out",
        }
    },
    paperContent: {
        padding: 16,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        flex: 1,
    },
    logo: {
        width: "max-content",
        alignSelf: "flex-end"
    },
    drag_drop_container: {
        overflowX: "hidden",
        overflowY: "hidden",
        flexDirection: "column",
        display: "flex",
        padding: 8,
        cursor: "pointer",
        '&:focus': {
            outline: 'none'
        }
    },
    drag_drop_container_compact: {
        overflowX: "hidden",
        overflowY: "hidden",
        flexDirection: "column",
        display: "flex",
        height: 150,
        padding: 8,
        cursor: "pointer",
        '&:focus': {
            outline: 'none'
        }
    }
}));

let uploadedFiles = [];
let xhrs = [];
let uploadStatusTimer = 0;


export default function UploadForm(props) {
    const dispatch = useContext(DispatchContext);
    const state = useContext(AppContext);
    const { id } = useParams();
    const classes = useStyles();
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [projectCodeValid, setProjectCodeValid] = useState(false);
    const [submissionState, setSubmissionState] = useState("upload"); // one of upload, submitted, skipped
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmDataCorrect, setConfirmDataCorrect] = useState(false);
    const [submitterData, setSubmitterData] = useState({
        documentType: "",
        namePrefix: "",
        name: "",
        nameSuffix: "",
        workTitle: "",
        studyProgram: "",
        supervisor: "",
        email: ""
    });
    const s = state.strings;

    useEffect(() => {
        return () => {
            onCancelAllFilesUpload()
        };
    }, []);


    useEffect(() => {
        if (!state.user.loggedIn) {
            showCaptcha();
        }
        getUploadForm(id);
    }, []);

    function getUploadForm(id) {
        setLoading(true);
        Service.getUploadForm(id).then(res => {
            setLoading(false);
            setProjectCodeValid(res.success);
            if (res.success) {
                setFormData(res.data)
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });
    }

    const onSubmitButtonClicked = () => {
        // Show confirmation dialog if metadata or email is present
        if (formData.RequestMetadata || (formData.AutoPlagCheck && formData.SendReport)) {
            setConfirmDialogOpen(true);
        } else {
            captchaAction("submitFile", submitFile);
        }
    }

    const onConfirmSubmit = () => {
        setConfirmDialogOpen(false);
        captchaAction("submitFile", submitFile);
    }

    const submitFile = () => {
        setLoading(true);
        
        // Build author from prefix, name, and suffix
        const author = [submitterData.namePrefix, submitterData.name, submitterData.nameSuffix]
            .map(s => s?.trim())
            .filter(Boolean)
            .join(" ");
        
        // Build comment from document type and study program
        const documentTypeLabel = submitterData.documentType === "bachelor" ? (s.uploadFormular.bachelorThesis || "Bachelor Thesis") :
            submitterData.documentType === "master" ? (s.uploadFormular.masterThesis || "Master Thesis") :
            submitterData.documentType === "other" ? (s.uploadFormular.otherDocument || "Other Document") : "";
        const comment = [documentTypeLabel, submitterData.studyProgram?.trim(), submitterData.supervisor?.trim()]
            .filter(Boolean)
            .join(" / ");
        
        Service.AddTextsToLibrary({
            uploadids: uploadedFiles,
            projectcode: id,
            name: submitterData.workTitle?.trim() || "",
            author: author,
            comment: comment,
            submittermail: submitterData.email?.trim() || "",
            autoplagcheck: formData.AutoPlagCheck ? 1 : 0,
        }).then(res => {
            onCancelAllFilesUpload();
            setLoading(false);
            setConfirmDataCorrect(false);  // Reset confirmation flag after submission
            if (res.success) {
                if (res.message.includes("textsSkipped")) {
                    dispatch(actions.openToast({ type: "warning", message: s.newCheck.textSkipped }));
                    setSubmissionState("skipped");
                } else {
                    dispatch(actions.openToast({ message: s.newCheck.textSubmitted }));
                    setSubmissionState("submitted");
                }
            } else {
                // Handle multiple errors by getting unique error messages
                const errors = res.errors && res.errors.length > 0 ? res.errors : [s.notificationMessage.unknownError || 'Unknown error'];
                const uniqueErrors = [...new Set(errors)];
                const errorMessage = uniqueErrors.map(err => resolveNotificationMessage(err, s.notificationMessage)).join(', ');
                dispatch(actions.openToast({ type: "error", message: errorMessage, status: true }));
            }
        });
    }

    const onNewButtonClicked = () => {
        onCancelAllFilesUpload();
        setLoading(false);
        setSubmissionState("upload");
    }


    const onDrop = useCallback(acceptedFiles => {
        let maxAllowedCount = config.maxNumberFileUploadForm - uploadedFiles.length;
        if (acceptedFiles.length > maxAllowedCount) {
            let skippedCount = acceptedFiles.length - maxAllowedCount;
            dispatch(actions.openToast({ type: "warning", message: s.newCheck.filesTruncated.format(config.maxNumberFileUploadForm, skippedCount), status: true }));
            acceptedFiles = acceptedFiles.slice(0, maxAllowedCount);
        }

        acceptedFiles.map(file => {
            const uuid = createUUID();
            file.id = uuid;
        })

        // Prefill workTitle with filename (without extension) if empty
        if (acceptedFiles.length > 0 && !submitterData.workTitle) {
            const fileName = acceptedFiles[0].name;
            const titleFromFile = fileName.replace(/\.[^/.]+$/, ""); // Remove file extension
            setSubmitterData(prev => ({ ...prev, workTitle: titleFromFile }));
        }

        dispatch(actions.setUploadedFiles(acceptedFiles));

        acceptedFiles.map((file, i) => {
            const uuid = file.id;
            const formData = new FormData();
            formData.append('file', file);
            formData.append('uploadid', uuid);
            formData.append('projectcode', id);
            if (document.getElementById(uuid)) {
                ReactDOM.render(<LinearProgress variant="determinate" value={0} />, document.getElementById(uuid))
            }
            let xhr = new XMLHttpRequest();
            xhrs.push(xhr);

            xhr.upload.onprogress = (e) => {
                const pct = +((e.loaded / e.total) * 100).toFixed(2);
                if (document.getElementById(uuid + '_status')) {
                    ReactDOM.render(`${formatFileSize(e.loaded)} / ${formatFileSize(e.total)} (${pct})%`, document.getElementById(uuid + '_status'));
                }
                if (document.getElementById(uuid)) {
                    ReactDOM.render(pct !== 100 ? <LinearProgress variant="determinate" value={pct} /> : <LinearProgress value={pct} />, document.getElementById(uuid))
                }
            };
            uploadedFiles.push(uuid)
            xhr.onreadystatechange = () => {

                if (xhr.readyState !== 4) return;   // 4: DONE
                if (xhr.status !== 200) {
                    return;
                }
                const response = JSON.parse(xhr.response)
                if (response && !response.success) {
                    onUploadedDeleteFile(uuid);
                    dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(response.errors[0], s.notificationMessage), status: true }))
                }
                /*handle success*/
            };

            xhr.open("post", config.backendUrl + "api/UploadFile");
            const token = getCsrfToken();
            if (token) xhr.setRequestHeader('X-CSRF-Token', token);
            const sid = getCookie(config.sessionCookieName);
            if (sid) xhr.setRequestHeader('X-Session-Id', sid);
            xhr.send(formData);
        });

        getUploadedFilesStatus(uploadedFiles)

    }, []);

    const onUploadedDeleteFile = (id) => {
        // todo: abort xhr for this file
        
        // Clear workTitle if it matches the deleted file's name (without extension)
        const deletedFile = state.uploadedFiles.find(f => f.id === id);
        if (deletedFile) {
            const titleFromFile = deletedFile.name.replace(/\.[^/.]+$/, "");
            if (submitterData.workTitle === titleFromFile) {
                setSubmitterData(prev => ({ ...prev, workTitle: "" }));
            }
        }
        
        uploadedFiles = uploadedFiles.filter(file => file !== id);
        if (uploadedFiles.length > 0) {
            getUploadedFilesStatus(uploadedFiles)
        }
        else {
            onCancelAllFilesUpload();
        }
        dispatch(actions.removeUploadedFile(id));
    }

    const getUploadedFilesStatus = (uploadids) => {

        clearInterval(uploadStatusTimer);

        uploadStatusTimer = setInterval(() => {
            Service.getUploadedFilesStatus(uploadids).then(res => {

                res.data.Files && res.data.Files.filter(f => f.Status == "Error").map(f => {
                    onUploadedDeleteFile(f.UploadId);
                    dispatch(actions.openToast({ type: "error", message: s.newCheck.uploadError, status: true }))
                });

                if (res.data.PendingCount === 0 && res.total !== 0 && uploadids.length === res.total) {
                    clearInterval(uploadStatusTimer);
                }
                dispatch(actions.setUploadedFilesStatus(res.data))
                // check if there are files in status=Error and delete them from the list

            })
        }, 2500);
    }

    const onCancelAllFilesUpload = () => {
        xhrs.map((x) => x.abort());
        xhrs = [];
        uploadedFiles = [];
        clearInterval(uploadStatusTimer);
        dispatch(actions.cancelFilesUpload());
        dispatch(actions.setUploadedFilesStatus({ Files: [] }))
    }

    const captchaAction = (action, fn) => {
        const captchas = document.getElementsByClassName("grecaptcha-badge");
        if (captchas.length > 0 && captchas[0].style.visibility != "hidden") {
            window.grecaptcha.ready(() => {
                window.grecaptcha.execute(config.captchaKey, { action: action }).then(function (token) {
                    Service.verifyReCaptcha(token).then((verifyRes) => {
                        if (verifyRes.success) {
                            fn();
                        } else {
                            setLoading(false);
                            dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(verifyRes.errors[0], s.notificationMessage) }));
                        }
                    });
                });
            });
        } else {
            fn();
        }
    }

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
    const uploadedOkCount = state.uploadedFilesStatus.filter(file => file.Status == "Ok").length;
    
    // Email validation helper
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };
    
    // Check if mandatory metadata fields are filled when metadata is requested
    const metadataValid = !formData.RequestMetadata || (
        submitterData.documentType &&
        submitterData.name.trim().length >= 5 &&
        submitterData.workTitle.trim().length >= 5
    );
    
    // Check if email is required and valid
    const emailRequired = formData.AutoPlagCheck && formData.SendReport;
    const emailValid = !emailRequired || isValidEmail(submitterData.email);

    return (
        <FullScreenPage>
            <Paper className={classes.paper}>
                <LoadingScreen status={loading} />
                {!projectCodeValid && !loading && <IconLabel centered type="warning" label={s.notificationMessage.projectCodeNotValid} />}
                {projectCodeValid && !loading &&
                    <div className={classes.paperContent}>
                        <img className={classes.logo} src={config.backendUrl + formData.logo} alt="logo" />
                        <Typography paragraph variant="h6" color="primary">{formData.header}</Typography>
                        <Typography variant="body2" color="secondary">{formData.description}</Typography>
                        {(formData.StartDate || formData.EndDate) && (
                            <Box display="flex" alignItems="center" style={{ marginTop: 8 }}>
                                <WarningIcon style={{ color: "#FF9800", marginRight: 8 }} />
                                <Typography variant="body2" style={{ color: "orange" }}>
                                    {formData.StartDate && formData.EndDate
                                        ? `${s.uploadFormular.submissionInterval}: ${timeConverter(formData.StartDate, null, s.common.dateFormat)} – ${timeConverter(formData.EndDate, null, s.common.dateFormat)}`
                                        : formData.StartDate
                                            ? `${s.uploadFormular.submissionInterval}: ${s.uploadFormular.from} ${timeConverter(formData.StartDate, null, s.common.dateFormat)}`
                                            : `${s.uploadFormular.submissionInterval}: ${s.uploadFormular.until} ${timeConverter(formData.EndDate, null, s.common.dateFormat)}`}
                                </Typography>
                            </Box>
                        )}

                        <Divider style={{ marginTop: 8, marginBottom: 16 }} />

                        {submissionState == "submitted" &&
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", padding: 40 }}>
                                <CheckedIcon style={{ fill: '#009900' }} height={100} width={100} />
                                <Box mt={2} />
                                <Typography paragraph variant="h6">{s.uploadFormular.thankYou}</Typography>
                                <Typography align="center" color="secondary">{s.uploadFormular.textSubmitted}</Typography>
                            </div>
                        }
                        {submissionState == "skipped" &&
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", padding: 40 }}>
                                <FileErrorIcon style={{ fill: '#FF9800' }} height={100} width={100} />
                                <Box mt={2} />
                                <Typography paragraph variant="h6">{s.uploadFormular.alreadyExisting}</Typography>
                                <Typography align="center" color="secondary">{s.uploadFormular.textSkipped}</Typography>
                            </div>
                        }
                        {submissionState == "upload" &&
                            (formData.SubmissionAllowed ?

                                <div className={formData.RequestMetadata ? classes.drag_drop_container_compact : classes.drag_drop_container} style={!formData.RequestMetadata ? { height: 260, minHeight: 110, overflow: "hidden" } : { minHeight: 110, overflow: "hidden" }} {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    {state.uploadedFiles.length > 0 ?
                                        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%", overflow: "hidden" }}>
                                            <FileUploadCards singleFile acceptedFiles={state.uploadedFiles} fileStatus={state.uploadedFilesStatus} onUploadedDeleteFile={onUploadedDeleteFile} isDragActive={isDragActive} />
                                        </div> :
                                        isDragActive ?
                                            <div style={{ borderRadius: 5, border: "2px solid #0D4A81", backgroundColor: "#eeeeee", width: "100%", height: "100%", display: "flex", flexDirection: formData.RequestMetadata ? "row" : "column", justifyContent: "center", alignItems: "center" }}>
                                                <UploadingIcon fill="#1982DD" height={formData.RequestMetadata ? 60 : 100} width={formData.RequestMetadata ? 60 : 100} style={formData.RequestMetadata ? { marginRight: 24 } : { marginBottom: 16 }} />
                                                <Typography align="center">{s.newCheck.dropHere}</Typography>
                                            </div> :
                                            <div style={{ borderRadius: 5, border: "2px dotted #0D4A81", width: "100%", height: "100%", display: "flex", flexDirection: formData.RequestMetadata ? "row" : "column", justifyContent: "center", alignItems: "center" }}>
                                                <UploadingIcon fill="#0D4A81" height={formData.RequestMetadata ? 60 : 100} width={formData.RequestMetadata ? 60 : 100} style={formData.RequestMetadata ? { marginRight: 24, flexShrink: 0 } : { marginBottom: 16 }} />
                                                <div style={formData.RequestMetadata ? { display: "flex", flexDirection: "column" } : {}}>
                                                    <Typography paragraph={!formData.RequestMetadata} align={formData.RequestMetadata ? "left" : "center"}>{s.dragYourFileHere}</Typography>
                                                    <Typography variant="body2" color="secondary" align={formData.RequestMetadata ? "left" : "center"}>{s.newCheck.parallelUpload.format(config.maxNumberFileUploadForm)}</Typography>
                                                    <Typography variant="body2" color="secondary" align={formData.RequestMetadata ? "left" : "center"}>{s.newCheck.supportFileFormats}</Typography>
                                                    <Typography variant="body2" color="secondary" align={formData.RequestMetadata ? "left" : "center"}>{s.newCheck.maxFileSize}</Typography>
                                                </div>
                                            </div>
                                    }
                                </div>
                                :
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", padding: 40 }}>
                                    <NoSubmissionIcon style={{ fill: '#FF9800' }} height={100} width={100} />
                                    <Box mt={2} />
                                    <Typography paragraph variant="h6">{s.uploadFormular.noSubmission}</Typography>
                                    <Typography align="center" color="secondary">{s.uploadFormular.notInSubmissionPeriod}</Typography>
                                </div>
                            )
                        }

                        <Divider style={{ marginTop: 8, marginBottom: 16 }} />

                        {submissionState == "upload" && formData.SubmissionAllowed && formData.RequestMetadata && (
                            <>
                                <DividerLabel label={s.uploadFormular.submitterInfo || "Submitter Information"} />
                                <Grid container spacing={2} style={{ marginBottom: 16 }}>
                                    {/* Row 1: Title (2/3) + Document Type (1/3) */}
                                    <Grid item xs={12} sm={8}>
                                        <TextField
                                            fullWidth
                                            required
                                            size="small"
                                            label={s.uploadFormular.workTitle || "Title of Work"}
                                            value={submitterData.workTitle}
                                            onChange={(e) => setSubmitterData({ ...submitterData, workTitle: e.target.value })}
                                            error={submitterData.workTitle && submitterData.workTitle.trim().length < 5}
                                            helperText={submitterData.workTitle && submitterData.workTitle.trim().length < 5 ? (s.uploadFormular.titleMinLength || "Title must be at least 5 characters") : ""}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            select
                                            fullWidth
                                            required
                                            size="small"
                                            label={s.uploadFormular.documentType || "Document Type"}
                                            value={submitterData.documentType}
                                            onChange={(e) => setSubmitterData({ ...submitterData, documentType: e.target.value })}
                                        >
                                            <MenuItem value="">{s.uploadFormular.selectDocumentType || "Please select..."}</MenuItem>
                                            <MenuItem value="bachelor">{s.uploadFormular.bachelorThesis || "Bachelor Thesis"}</MenuItem>
                                            <MenuItem value="master">{s.uploadFormular.masterThesis || "Master Thesis"}</MenuItem>
                                            <MenuItem value="other">{s.uploadFormular.otherDocument || "Other Document"}</MenuItem>
                                        </TextField>
                                    </Grid>
                                    {/* Row 2: Prefix + Author Name + Suffix */}
                                    <Grid item xs={12} sm={2}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={s.uploadFormular.namePrefix || "Prefix"}
                                            placeholder={s.uploadFormular.namePrefixPlaceholder || "e.g. Dr., Prof."}
                                            value={submitterData.namePrefix}
                                            onChange={(e) => setSubmitterData({ ...submitterData, namePrefix: e.target.value })}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <TextField
                                            fullWidth
                                            required
                                            size="small"
                                            label={s.uploadFormular.submitterName || "Author Name"}
                                            value={submitterData.name}
                                            onChange={(e) => setSubmitterData({ ...submitterData, name: e.target.value })}
                                            error={submitterData.name && submitterData.name.trim().length < 5}
                                            helperText={submitterData.name && submitterData.name.trim().length < 5 ? (s.uploadFormular.nameMinLength || "Name must be at least 5 characters") : ""}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={s.uploadFormular.nameSuffix || "Suffix"}
                                            placeholder={s.uploadFormular.nameSuffixPlaceholder || "e.g. M.Sc., B.A."}
                                            value={submitterData.nameSuffix}
                                            onChange={(e) => setSubmitterData({ ...submitterData, nameSuffix: e.target.value })}
                                        />
                                    </Grid>
                                    {/* Row 3: Study Program + Supervisor */}
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={s.uploadFormular.studyProgram || "Study Program"}
                                            value={submitterData.studyProgram}
                                            onChange={(e) => setSubmitterData({ ...submitterData, studyProgram: e.target.value })}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={s.uploadFormular.supervisor || "Betreuer"}
                                            value={submitterData.supervisor}
                                            onChange={(e) => setSubmitterData({ ...submitterData, supervisor: e.target.value })}
                                        />
                                    </Grid>
                                </Grid>
                            </>

                        )}

                        {submissionState == "upload" && formData.SubmissionAllowed && formData.AutoPlagCheck && formData.SendReport && (
                            <Grid container spacing={2} style={{ marginBottom: 16 }}>
                                <Grid item xs={12}>
                                    <Box display="flex" alignItems="flex-start">
                                        <EmailIcon color="action" style={{ marginRight: 12, marginTop: 12 }} />
                                        <TextField
                                            fullWidth
                                            required
                                            size="small"
                                            type="email"
                                            label={s.uploadFormular.emailPlaceholder || "A copy of the plagiarism report will be sent to this address"}
                                            placeholder={s.uploadFormular.emailAddress || "E-Mail Address"}
                                            value={submitterData.email}
                                            onChange={(e) => setSubmitterData({ ...submitterData, email: e.target.value })}
                                            error={submitterData.email && !isValidEmail(submitterData.email)}
                                            helperText={submitterData.email && !isValidEmail(submitterData.email) 
                                                ? (s.uploadFormular.invalidEmail || "Please enter a valid email address")
                                                : ""}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        )}

                        {submissionState == "upload" ?
                            <Button onClick={onSubmitButtonClicked} disabled={!uploadedOkCount || !metadataValid || !emailValid} style={{ alignSelf: "center", width: 300 }} variant="contained" color="primary">{s.newCheck.submitFile}</Button>
                            :
                            <Button onClick={onNewButtonClicked} style={{ alignSelf: "center", width: 300 }} variant="contained" color="primary">{s.uploadFormular.uploadOtherFile}</Button>
                        }
                    </div>
                }

            </Paper>

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{s.uploadFormular.confirmSubmission || "Confirm Submission"}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="secondary" style={{ marginBottom: 16 }}>
                        {s.uploadFormular.confirmSubmissionHint || "Please verify your submission details before submitting."}
                    </Typography>
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell style={{ fontWeight: 500, width: 180, whiteSpace: "nowrap" }}>{s.common.file || "File"}</TableCell>
                                <TableCell>{state.uploadedFiles.length > 0 ? state.uploadedFiles[0].name : "-"}</TableCell>
                            </TableRow>
                            {formData.RequestMetadata && (
                                <>
                                    <TableRow>
                                        <TableCell style={{ fontWeight: 500, whiteSpace: "nowrap" }}>{s.uploadFormular.documentType || "Document Type"}</TableCell>
                                        <TableCell>
                                            {submitterData.documentType === "bachelor" ? (s.uploadFormular.bachelorThesis || "Bachelor Thesis") :
                                             submitterData.documentType === "master" ? (s.uploadFormular.masterThesis || "Master Thesis") :
                                             submitterData.documentType === "other" ? (s.uploadFormular.otherDocument || "Other Document") : "-"}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ fontWeight: 500 }}>{s.uploadFormular.submitterName || "Author Name"}</TableCell>
                                        <TableCell>
                                            {[submitterData.namePrefix, submitterData.name, submitterData.nameSuffix].filter(Boolean).join(" ") || "-"}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ fontWeight: 500 }}>{s.uploadFormular.workTitle || "Title of Work"}</TableCell>
                                        <TableCell>{submitterData.workTitle || "-"}</TableCell>
                                    </TableRow>
                                    {submitterData.studyProgram && (
                                        <TableRow>
                                            <TableCell style={{ fontWeight: 500 }}>{s.uploadFormular.studyProgram || "Study Program"}</TableCell>
                                            <TableCell>{submitterData.studyProgram}</TableCell>
                                        </TableRow>
                                    )}
                                    {submitterData.supervisor && (
                                        <TableRow>
                                            <TableCell style={{ fontWeight: 500 }}>{s.uploadFormular.supervisor || "Betreuer"}</TableCell>
                                            <TableCell>{submitterData.supervisor}</TableCell>
                                        </TableRow>
                                    )}
                                </>
                            )}
                            {formData.AutoPlagCheck && formData.SendReport && submitterData.email && (
                                <TableRow>
                                    <TableCell style={{ fontWeight: 500 }}>{s.uploadFormular.emailAddress || "E-Mail Address"}</TableCell>
                                    <TableCell>{submitterData.email}</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <Box mt={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={confirmDataCorrect}
                                    onChange={(e) => setConfirmDataCorrect(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={s.uploadFormular.confirmDataCorrect || "I confirm the correctness of the data"}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setConfirmDialogOpen(false); setConfirmDataCorrect(false); }} color="primary">
                        {s.common.cancel || "Cancel"}
                    </Button>
                    <Button onClick={onConfirmSubmit} variant="contained" color="primary" disabled={!confirmDataCorrect}>
                        {s.newCheck.submitFile || "Submit"}
                    </Button>
                </DialogActions>
            </Dialog>
        </FullScreenPage>);
}
